import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from './firebase-config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  serverTimestamp, 
  onSnapshot, 
  orderBy, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  arrayUnion
} from 'firebase/firestore';
import { useGesture } from '@use-gesture/react';
import { useNavigate, Navigate } from 'react-router-dom';
import './ChatPage.css';
import LoadingSpinner from './LoadingSpinner';
import LetterAvatar from './LetterAvatar';

const ChatPage = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userRole, setUserRole] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isConversationsLoading, setIsConversationsLoading] = useState(true);
  const [user] = useState(auth.currentUser);
  const [jobDetails, setJobDetails] = useState(null);
  const [swipingConversation, setSwipingConversation] = useState(null);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const deleteThreshold = -80;
  const [deletingConversations, setDeletingConversations] = useState(new Set());
  const [employeeApplications, setEmployeeApplications] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteConversation, setPendingDeleteConversation] = useState(null);

  const bindChatView = useGesture({
    onDrag: ({ down, movement: [mx], direction: [dx] }) => {
      if (!down && dx > 0 && mx > 80) { // on release, if swiped right more than 80px
        setSelectedConversation(null);
      }
    },
  }, {
    drag: { axis: 'x' }
  });

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const employeeDocRef = doc(db, 'employees', user.uid);
        const employeeDocSnap = await getDoc(employeeDocRef);
        
        if (employeeDocSnap.exists()) {
          setUserRole('employee');
        } else {
          const employerDocRef = doc(db, 'employers', user.uid);
          const employerDocSnap = await getDoc(employerDocRef);
          if (employerDocSnap.exists()) {
            setUserRole('employer');
          }
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const fetchConversations = async () => {
      try {
        setIsConversationsLoading(true);
        const conversationsRef = collection(db, 'conversations');
        const q = query(
          conversationsRef,
          where('participants', 'array-contains', user.uid),
          orderBy('lastMessageTime', 'desc')
        );

        const unsubscribe = onSnapshot(q, async (snapshot) => {
          const conversationsData = await Promise.all(snapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            const otherUserId = data.participants.find(id => id !== user.uid);
            
            let jobInfo = null;
            if (data.jobId) {
              try {
                const jobDocRef = doc(db, 'jobs', data.jobId);
                const jobDocSnap = await getDoc(jobDocRef);
                if (jobDocSnap.exists()) {
                  jobInfo = { id: jobDocSnap.id, ...jobDocSnap.data() };
                }
              } catch (error) {
                console.error('Error fetching job info:', error);
              }
            }

            let employeeDetails = null;
            if (userRole === 'employer' && otherUserId) {
              try {
                const employeeDocRef = doc(db, 'employees', otherUserId);
                const employeeDocSnap = await getDoc(employeeDocRef);
                if (employeeDocSnap.exists()) {
                  employeeDetails = employeeDocSnap.data();
                }
              } catch (error) {
                console.error('Error fetching employee details:', error);
              }
            }

            // Get other user's name and picture based on their role
            let otherUserName = '';
            let otherUserPic = '';

            if (userRole === 'employer') {
              if (employeeDetails) {
                otherUserName = employeeDetails.fullName || 'Unknown User';
                otherUserPic = employeeDetails.profilePic;
              }
            } else {
              // For employees viewing employer
              try {
                const employerDocRef = doc(db, 'employers', otherUserId);
                const employerDocSnap = await getDoc(employerDocRef);
                if (employerDocSnap.exists()) {
                  const employerData = employerDocSnap.data();
                  otherUserName = employerData.companyName;
                  otherUserPic = employerData.profilePic;
                }
              } catch (error) {
                console.error('Error fetching employer details:', error);
              }
            }

            return {
              id: docSnapshot.id,
              ...data,
              jobInfo,
              employeeDetails,
              lastMessageTime: data.lastMessageTime?.toDate(),
              otherUserName: otherUserName || 'Unknown User',
              otherUserPic: otherUserPic || 'https://via.placeholder.com/40'
            };
          }));
          setConversations(conversationsData);
          setIsConversationsLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error fetching conversations:', error);
        setIsConversationsLoading(false);
      }
    };

    fetchConversations();
  }, [user, userRole]);

  useEffect(() => {
    if (selectedConversation) {
      const messagesRef = collection(db, 'conversations', selectedConversation.id, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'asc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messagesList = snapshot.docs.map(docSnapshot => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
          timestamp: docSnapshot.data().timestamp?.toDate()
        }));
        setMessages(messagesList);
      });

      if (selectedConversation.jobInfo) {
        setJobDetails(selectedConversation.jobInfo);
      }

      // Mark messages as read when conversation is selected
      markMessagesAsRead(selectedConversation.id);

      return () => unsubscribe();
    }
  }, [selectedConversation]);

  // Function to mark messages as read
  const markMessagesAsRead = async (conversationId) => {
    if (!user) return;
    
    try {
      console.log('Marking messages as read for conversation:', conversationId);
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);
      
      if (conversationDoc.exists()) {
        const data = conversationDoc.data();
        
        // Only update if there's a last message and it's not from the current user
        if (data.lastMessageSenderId && data.lastMessageSenderId !== user.uid) {
          const readBy = data.readBy || [];
          
          // Only update if the current user hasn't read the messages yet
          if (!readBy.includes(user.uid)) {
            await updateDoc(conversationRef, {
              readBy: arrayUnion(user.uid)
            });
            console.log('Messages marked as read successfully');
          }
        }
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  useEffect(() => {
    // Fetch applications for employer when a conversation is selected
    const fetchEmployeeApplications = async () => {
      if (
        userRole === 'employer' &&
        selectedConversation &&
        selectedConversation.participants &&
        selectedConversation.jobInfo
      ) {
        const employeeId = selectedConversation.participants.find(id => id !== user.uid);
        const employerId = user.uid;
        const q = query(
          collection(db, 'applications'),
          where('employeeId', '==', employeeId),
          where('employerId', '==', employerId),
          where('status', '==', 'pending')
        );
        const snapshot = await getDocs(q);
        const apps = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setEmployeeApplications(apps);
        if (apps.length === 1) setSelectedAppId(apps[0].id);
        else setSelectedAppId('');
      } else {
        setEmployeeApplications([]);
        setSelectedAppId('');
      }
    };
    fetchEmployeeApplications();
  }, [userRole, selectedConversation, user]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const messageRef = collection(db, 'conversations', selectedConversation.id, 'messages');
      await addDoc(messageRef, {
        text: newMessage,
        senderId: user.uid,
        timestamp: serverTimestamp()
      });

      console.log('Sending message from user:', user.uid, 'in conversation:', selectedConversation.id);
      
      const conversationRef = doc(db, 'conversations', selectedConversation.id);
      await updateDoc(conversationRef, {
        lastMessage: newMessage,
        lastMessageTime: serverTimestamp(),
        lastMessageSenderId: user.uid,
        readBy: [user.uid] // Reset readBy to only include the sender
      });
      
      console.log('Message sent successfully, lastMessageSenderId set to:', user.uid);

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const startNewConversation = async (userId) => {
    if (!user) return;

    try {
      const existingConversation = conversations.find(conv => 
        conv.participants.includes(userId)
      );

      if (existingConversation) {
        setSelectedConversation(existingConversation);
      } else {
        const conversationsRef = collection(db, 'conversations');
        const newConversationRef = await addDoc(conversationsRef, {
          participants: [user.uid, userId],
          createdAt: serverTimestamp()
        });
        setSelectedConversation({ 
          id: newConversationRef.id, 
          participants: [user.uid, userId] 
        });
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  const handleTouchStart = (e, conversationId) => {
    setSwipingConversation(conversationId);
    setStartX(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!swipingConversation) return;
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchEnd = async (conversationId) => {
    if (!swipingConversation) return;
    
    const diff = currentX - startX;
    if (diff < deleteThreshold) {
      // Show confirmation popup instead of immediately deleting
      setPendingDeleteConversation(conversationId);
      setShowDeleteConfirm(true);
    }
    
    setSwipingConversation(null);
    setStartX(0);
    setCurrentX(0);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteConversation) return;
    
    const conversationId = pendingDeleteConversation;
    
    // Start the deletion animation
    setDeletingConversations(prev => new Set([...prev, conversationId]));
    
    // Wait for animation to complete before actually deleting
    setTimeout(async () => {
      try {
        const conversationRef = doc(db, 'conversations', conversationId);
        await deleteDoc(conversationRef);
        setDeletingConversations(prev => {
          const newSet = new Set(prev);
          newSet.delete(conversationId);
          return newSet;
        });
      } catch (error) {
        console.error('Error deleting conversation:', error);
        // Remove from deleting set if there's an error
        setDeletingConversations(prev => {
          const newSet = new Set(prev);
          newSet.delete(conversationId);
          return newSet;
        });
      }
    }, 500); // Match this with the CSS animation duration
    
    setShowDeleteConfirm(false);
    setPendingDeleteConversation(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setPendingDeleteConversation(null);
  };

  const getSwipeStyles = (conversationId) => {
    if (swipingConversation !== conversationId) return {};
    
    const diff = currentX - startX;
    const translateX = Math.min(0, Math.max(diff, -100));
    return {
      transform: `translateX(${translateX}px)`,
    };
  };

  const isDeleteThreshold = (conversationId) => {
    if (swipingConversation !== conversationId) return false;
    return (currentX - startX) < deleteThreshold;
  };

  const handleActionButtonClick = (action) => {
    if (action.type === 'viewProfile') {
      // Store current URL to return after viewing profile
      sessionStorage.setItem('returnToChat', window.location.pathname);
      navigate(`/profile/${action.employeeId}`);
    } else if (action.type === 'viewJob') {
      // Store current URL to return after viewing job
      sessionStorage.setItem('returnToChat', window.location.pathname);
      navigate(`/job/${action.jobId}`);
    }
  };

  const renderMessage = (message) => {
    // Custom system notification for new match
    if (message.isSystemMessage && message.text?.toLowerCase().includes('new match')) {
      // Try to extract details from message fields or fallback to parsing text
      const employeeName = message.employeeName || (message.text.match(/for (.+?)(!|$)/i)?.[1] || 'New Match');
      const jobTitle = message.jobTitle || (message.text.match(/for (.+?)(!|$)/i)?.[1] || '');
      const location = message.location || '';
      const resumeUrl = message.resumeUrl;
      const employeeId = message.employeeId;
      const jobId = message.jobId;
      return (
        <div className="system-match-notification">
          <div className="system-match-header">
            <span role="img" aria-label="celebrate">🎉</span> New Match!
          </div>
          <div className="system-match-body">
            <div><strong>{employeeName}</strong>{jobTitle && <> matched for <strong>{jobTitle}</strong></>}</div>
            {location && <div>Location: {location}</div>}
          </div>
          <div className="system-match-actions">
            {userRole === 'employee' && jobId && (
              <button
                className="message-action-button"
                onClick={() => handleActionButtonClick({ type: 'viewJob', jobId })}
              >
                View Job Details
              </button>
            )}
            {userRole === 'employer' && resumeUrl && (
              <button
                className="message-action-button"
                onClick={() => window.open(resumeUrl, '_blank')}
              >
                View Resume
              </button>
            )}
            {userRole === 'employer' && employeeId && (
              <button
                className="message-action-button"
                onClick={() => handleActionButtonClick({ type: 'viewProfile', employeeId })}
              >
                View Profile
              </button>
            )}
          </div>
        </div>
      );
    }
    const isCurrentUser = message.senderId === user?.uid;
    return (
      <div key={message.id} className={`message ${isCurrentUser ? 'sent' : 'received'}`}>
        <div className="message-avatar">
          {isCurrentUser ? (
            user?.photoURL ? (
              <img src={user.photoURL} alt="Your profile" />
            ) : (
              <LetterAvatar name={user?.displayName || 'You'} size={32} />
            )
          ) : (
            selectedConversation?.otherUserPic && 
            selectedConversation.otherUserPic !== 'https://via.placeholder.com/40' ? (
              <img 
                src={selectedConversation.otherUserPic} 
                alt={selectedConversation.otherUserName || 'Profile'} 
              />
            ) : (
              <LetterAvatar 
                name={selectedConversation?.otherUserName || 'Unknown'} 
                size={32}
              />
            )
          )}
        </div>
        <div className="message-content">
          {message.text}
          {message.isInitialMessage && message.jobInfo && (
            <div className="job-application-info">
              <h4>{message.jobInfo.title}</h4>
              <p>{message.jobInfo.company}</p>
              {message.jobInfo.resumeUrl && (
                <button 
                  className="view-resume-button"
                  onClick={() => window.open(message.jobInfo.resumeUrl, '_blank')}
                >
                  View Resume
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="chat-container">
      <div className={`sidebar ${selectedConversation ? 'hidden' : ''}`}>
        <div className="sidebar-header">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        <div className="conversation-list">
          {isConversationsLoading ? (
            <div className="loading-state">
              <LoadingSpinner />
            </div>
          ) : conversations.length === 0 ? (
            <div className="empty-chat">
              <div className="empty-chat-icon" role="img" aria-label="No messages">💬</div>
              <div className="empty-chat-title">No Messages Yet</div>
              <div className="empty-chat-subtitle">
                {userRole === 'employer' 
                  ? "Connect with applicants to start messaging and build your team."
                  : "Apply to jobs and your conversations with employers will show up here!"}
              </div>
            </div>
          ) : (
            conversations
              .filter(conv => 
                conv.otherUserName.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map(conversation => (
                <div
                  key={conversation.id}
                  className={`conversation-item 
                    ${swipingConversation === conversation.id ? 'swiping' : ''} 
                    ${isDeleteThreshold(conversation.id) ? 'delete-threshold' : ''}
                    ${deletingConversations.has(conversation.id) ? 'deleting' : ''}
                  `}
                  onClick={() => !swipingConversation && setSelectedConversation(conversation)}
                  onTouchStart={(e) => handleTouchStart(e, conversation.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={() => handleTouchEnd(conversation.id)}
                  style={getSwipeStyles(conversation.id)}
                >
                  <div className="conversation-avatar">
                    {conversation.otherUserPic && conversation.otherUserPic !== 'https://via.placeholder.com/40' ? (
                      <img 
                        src={conversation.otherUserPic} 
                        alt={conversation.otherUserName || 'Profile'} 
                      />
                    ) : (
                      <LetterAvatar 
                        name={conversation.otherUserName || 'Unknown'} 
                        size={50}
                      />
                    )}
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-header">
                      <h3 className="conversation-name">{conversation.otherUserName}</h3>
                      {conversation.lastMessageTime && (
                        <span className="conversation-time">
                          {new Date(conversation.lastMessageTime).toLocaleTimeString([], {
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </div>
                    <p className="last-message">
                      {conversation.lastMessage || 'No messages yet'}
                    </p>
                  </div>
                  {conversation.unreadCount > 0 && (
                    <span className="unread-count">{conversation.unreadCount}</span>
                  )}
                  <div className="delete-button">🗑️</div>
                </div>
              ))
          )}
        </div>
      </div>

      <div className={`chat-main ${selectedConversation ? 'active' : ''}`} {...bindChatView()}>
        {selectedConversation && (
          <>
            <div className="chat-header">
              <button className="back-button" onClick={() => setSelectedConversation(null)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="chat-header-info">
                <div className="chat-header-avatar">
                  {selectedConversation?.otherUserPic && 
                   selectedConversation.otherUserPic !== 'https://via.placeholder.com/40' ? (
                    <img 
                      src={selectedConversation.otherUserPic} 
                      alt={selectedConversation.otherUserName || 'Profile'} 
                    />
                  ) : (
                    <LetterAvatar 
                      name={selectedConversation?.otherUserName || 'Unknown'} 
                      size={40}
                    />
                  )}
                </div>
                <h2>{selectedConversation?.otherUserName}</h2>
              </div>
              {/* Only show for employer */}
              {userRole === 'employer' && (
                <>
                  <div className="accept-interview-topright">
                    <div className="accept-interview-group">
                      <select
                        className="accept-interview-select"
                        value={selectedAppId}
                        onChange={e => setSelectedAppId(e.target.value)}
                      >
                        <option value="" disabled>Select a position</option>
                        {employeeApplications.length > 0 ? (
                          employeeApplications.map(app => (
                            <option key={app.id} value={app.id}>{app.jobTitle || app.id}</option>
                          ))
                        ) : (
                          <option value="none">No positions</option>
                        )}
                      </select>
                      <button
                        className="accept-interview-btn"
                        onClick={() => setShowComingSoon(true)}
                      >
                        Accept for Interview
                      </button>
                    </div>
                  </div>
                  {/* Coming Soon Popup */}
                  {showComingSoon && (
                    <div className="coming-soon-popup-overlay" onClick={() => setShowComingSoon(false)}>
                      <div className="coming-soon-popup" onClick={e => e.stopPropagation()}>
                        <span className="close-popup" onClick={() => setShowComingSoon(false)}>&times;</span>
                        <h3>Coming Soon</h3>
                        <p>This feature is coming soon!</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="messages-container">
              {messages.map((message) => (
                <div key={message.id}>
                  {renderMessage(message)}
                </div>
              ))}
            </div>
            <div className="chat-input-container">
              <form onSubmit={handleSendMessage} className="chat-input-form">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="chat-input"
                />
                <button type="submit" className="send-button">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </form>
            </div>
          </>
        )}
      </div>
      
      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal-content delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Conversation</h3>
            </div>
            <div className="modal-body">
              <p>Deleting this conversation will permanently remove all messages and prevent further contact with this {userRole === 'employer' ? 'applicant' : 'employer'}.</p>
              <div className="delete-confirm-buttons">
                <button className="delete-confirm-btn cancel" onClick={handleCancelDelete}>
                  Cancel
                </button>
                <button className="delete-confirm-btn confirm" onClick={handleConfirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
